import { getApiUri } from '@carlito/config';
import {
  ControllerExecutionResult,
  DomainError,
  IController,
  UseCaseDomainErrors,
  UseCaseError,
} from '@carlito/ddd';

import { SupportedLanguages } from '@carlito/intl';

let carlitoHubAccessToken: string | undefined;

export function setCarlitoHubAccessToken(token: string | undefined) {
  carlitoHubAccessToken = token;
}

const FEDERATION_API_URL = getApiUri('carlito_federation');

export type ApiRequestBaseParams = {
  accessToken?: string;
  apiKey?: string;
};

type RequestOptions =
  | ({
      queryParameters?: Record<string, string>;
    } & RequestInit &
      ApiRequestBaseParams)
  | undefined;

let language: SupportedLanguages | undefined;

function cleanQueryParameters(queryParameters?: Record<string, string>) {
  if (!queryParameters) {
    return undefined;
  }

  const cleanedQueryParameters: Record<string, string> = Object.entries(queryParameters).reduce(
    (acc, [key, value]) => {
      if (value) {
        return {
          ...acc,
          [key]: value,
        };
      }

      return acc;
    },
    {}
  );

  return cleanedQueryParameters;
}

export async function apiRequest<TController extends IController>(
  endpoint: string,
  options?: RequestOptions
) {
  const { queryParameters, apiKey, ...fetchOpts } = options || {};

  const languageHeader: HeadersInit = language ? { 'Accept-Language': language } : {};

  const accessToken = options?.accessToken ?? carlitoHubAccessToken;

  const response = await fetch(
    `${FEDERATION_API_URL}${endpoint}${
      queryParameters ? `?${new URLSearchParams(cleanQueryParameters(queryParameters))}` : ''
    }`,
    {
      ...fetchOpts,
      headers: {
        ...(fetchOpts.headers || {}),
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
        ...languageHeader,
        ...(apiKey
          ? {
              'api-key': apiKey,
            }
          : {}),
      },
    }
  );

  const reqBody = (await response.json()) as ControllerExecutionResult<TController>;

  if (reqBody.type !== 'success') {
    switch (reqBody.type) {
      case 'domain_error': {
        const domainErrors = reqBody.errors.map((e) => {
          const domainError = new DomainError({
            errorCode: e.code,
            message: e.message,
          });
          return domainError;
        });
        throw new UseCaseDomainErrors(domainErrors);
      }

      case 'use_case_error': {
        const error = new UseCaseError({
          errorCode: reqBody.errors[0].code,
          message: reqBody.errors[0].message,
        });
        throw error;
      }

      case 'controller_error':
      default:
        throw new Error('Unexpected error');
    }
  }

  if (!response.ok) {
    throw new Error('Unexpected error');
  }

  // Force success type, because we already checked it above;
  // an error would have been thrown otherwise
  type SuccessBody = Extract<ControllerExecutionResult<TController>, { type: 'success' }>;

  return reqBody as SuccessBody;
}
